import { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { formatBytes } from "../lib/constants";
import { User, Shield, Bell, HardDrive, Rocket, Package, ShieldCheck, ShieldOff, KeyRound, Loader2, Mail, FileText, Check, X, History } from "lucide-react";
import { toast } from "sonner";

import { _t, _locale } from "@/lib/i18n";
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(_locale(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifications(data.notifications || []);
      } catch {}
    })();
  }, []);

  const used = user?.storage_used || 0;
  const limit = user?.storage_limit || 200 * 1024 * 1024;
  const pct = Math.min(100, Math.round((used / limit) * 100));

  return (
    <AppLayout>
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Profilo")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-2">{_t("Il tuo hangar")}</h1>
        <p className="text-white/60">{_t("Gestisci profilo, sicurezza, consensi e archiviazione.")}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-10">
        <div className="tc-card p-6 md:col-span-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4"><User className="w-4 h-4" />{" "}{_t("Account")}</div>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <Field label={_t("Nome")} value={`${user?.name || ""} ${user?.surname || ""}`.trim() || "—"} />
            <Field label={_t("Username")} value={`@${user?.username}`} />
            <Field label={_t("Email")} value={user?.email} />
            <Field label={_t("Piano")} value={user?.plan?.toUpperCase()} />
          </div>
        </div>

        <div className="tc-card p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4"><HardDrive className="w-4 h-4" />{" "}{_t("Archiviazione")}</div>
          <div className="font-display text-3xl text-white mb-1">{formatBytes(used)}</div>
          <div className="text-xs text-white/50 mb-3">{_t("di {0}", formatBytes(limit))}</div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FF5722] to-[#FF8A50]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <TwoFactorSection user={user} refreshUser={refresh} />

      <ConsentSection refreshUser={refresh} />

      <div className="tc-card p-6 mb-10">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4"><Bell className="w-4 h-4" />{" "}{_t("Notifiche")}</div>
        {notifications.length === 0 ? (
          <div className="text-white/50 text-sm py-4">{_t("Nessuna notifica per ora.")}</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {notifications.map((n) => (
              <li key={n.id} className="py-3 flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                  {n.kind === "capsule_available" ? <Rocket className="w-4 h-4 text-[#FF5722]" /> : <Package className="w-4 h-4 text-white/60" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm">{n.title}</div>
                  <div className="text-white/50 text-xs">{n.body}</div>
                </div>
                <div className="text-white/40 text-xs shrink-0">{new Date(n.created_at).toLocaleDateString(_locale())}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="tc-card p-6 mb-10">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4"><Shield className="w-4 h-4" />{" "}{_t("Sessione")}</div>
        <button onClick={logout} className="btn-danger" data-testid="logout-all">{_t("Esci da tutti i dispositivi")}</button>
      </div>
    </AppLayout>
  );
}

/* ================================================================ */
/*                          2FA SECTION                              */
/* ================================================================ */
function TwoFactorSection({ user, refreshUser }) {
  const enabled = !!user?.two_factor_enabled;
  const [setupData, setSetupData] = useState(null); // { secret, qr_data_url }
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verifyEnable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success(_t("Autenticazione a due fattori attivata"));
      setSetupData(null); setCode("");
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password, code });
      toast.success(_t("2FA disattivato"));
      setCode(""); setPassword("");
      await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="tc-card p-6 mb-10" data-testid="two-factor-section">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
        <KeyRound className="w-4 h-4" />{" "}{_t("Verifica in due passaggi (2FA)")}</div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 mb-1">
            {enabled ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> : <ShieldOff className="w-5 h-5 text-white/40" />}
            <span className="text-white font-medium">{enabled ? _t("Attiva") : _t("Non attiva")}</span>
          </div>
          <p className="text-sm text-white/60 max-w-lg">{_t("Aggiungi un secondo livello di sicurezza al tuo account. Al login ti verrà chiesto un codice generato da un'app di autenticazione (Google Authenticator, Authy, 1Password…).")}</p>
        </div>
        {!enabled && !setupData && (
          <button onClick={startSetup} disabled={busy} className="btn-launch" data-testid="2fa-start-setup">{_t("{0} Attiva 2FA", busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />)}</button>
        )}
      </div>

      {!enabled && setupData && (
        <div className="border border-[#FF5722]/25 bg-[#FF5722]/[0.06] rounded-lg p-5 space-y-4">
          <div>
            <div className="text-sm text-white/80 mb-2">{_t("1. Scansiona questo QR con la tua app di autenticazione")}</div>
            <img src={setupData.qr_data_url} alt={_t("QR 2FA")} className="w-44 h-44 bg-white p-2 rounded-md" data-testid="2fa-qr" />
          </div>
          <div>
            <div className="text-xs text-white/50 mb-1">{_t("Oppure inserisci manualmente questo codice segreto:")}</div>
            <div className="font-mono-launch text-white/90 tracking-widest text-sm bg-black/40 border border-white/10 rounded px-3 py-2 select-all" data-testid="2fa-secret">{setupData.secret}</div>
          </div>
          <div>
            <label className="tc-label">{_t("2. Inserisci il codice a 6 cifre generato dall'app")}</label>
            <input
              data-testid="2fa-verify-code"
              type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="tc-input font-mono-launch text-2xl tracking-[0.4em] text-center max-w-xs"
              placeholder="000000"
            />
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setSetupData(null); setCode(""); }} className="btn-ghost">{_t("Annulla")}</button>
            <button onClick={verifyEnable} disabled={busy || code.length !== 6} className="btn-launch" data-testid="2fa-confirm">{_t("{0} Conferma e attiva", busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />)}</button>
          </div>
        </div>
      )}

      {enabled && (
        <details className="border border-white/10 rounded-lg p-4">
          <summary className="cursor-pointer text-sm text-white/70 hover:text-white">{_t("Disattiva 2FA")}</summary>
          <div className="mt-4 space-y-3 max-w-md">
            <div>
              <label className="tc-label">{_t("Password")}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="tc-input" data-testid="2fa-disable-password" />
            </div>
            <div>
              <label className="tc-label">{_t("Codice 2FA attuale")}</label>
              <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="tc-input font-mono-launch tracking-[0.3em]" placeholder="000000" data-testid="2fa-disable-code" />
            </div>
            <button onClick={disable} disabled={busy} className="btn-danger" data-testid="2fa-disable-submit">{_t("{0} Disattiva 2FA", busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />)}</button>
          </div>
        </details>
      )}
    </div>
  );
}

/* ================================================================ */
/*                       CONSENT MANAGEMENT                          */
/* ================================================================ */
function ConsentSection({ refreshUser }) {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/consent/status"),
        api.get("/consent/history"),
      ]);
      setStatus(s.data);
      setHistory(h.data.history || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggleMarketing = async (value) => {
    setBusy(true);
    try {
      await api.put("/consent/update", { marketing: value });
      toast.success(value ? _t("Consenso marketing attivato") : _t("Consenso marketing revocato"));
      await load(); await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reAccept = async (type) => {
    setBusy(true);
    try {
      await api.put("/consent/update", { [type]: true });
      toast.success(_t("Nuova versione accettata"));
      await load(); await refreshUser();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const c = status.consents;

  return (
    <div className="tc-card p-6 mb-10" data-testid="consent-section">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
        <FileText className="w-4 h-4" />{" "}{_t("Preferenze di consenso (GDPR)")}</div>

      <div className="space-y-3">
        <ConsentCard
          testId="consent-status-terms"
          icon={<ShieldCheck className="w-5 h-5 text-emerald-400" />}
          title={_t("Termini di servizio")}
          version={c.terms.version}
          currentVersion={c.terms.current_version}
          updatedAt={c.terms.updated_at}
          accepted={c.terms.accepted}
          required
          needsUpdate={c.terms.needs_update}
          onReAccept={() => reAccept("terms")}
          busy={busy}
        />
        <ConsentCard
          testId="consent-status-privacy"
          icon={<ShieldCheck className="w-5 h-5 text-emerald-400" />}
          title={_t("Informativa sulla privacy")}
          version={c.privacy.version}
          currentVersion={c.privacy.current_version}
          updatedAt={c.privacy.updated_at}
          accepted={c.privacy.accepted}
          required
          needsUpdate={c.privacy.needs_update}
          onReAccept={() => reAccept("privacy")}
          busy={busy}
        />
        <ConsentCard
          testId="consent-status-marketing"
          icon={<Mail className="w-5 h-5 text-white/60" />}
          title={_t("Comunicazioni promozionali e pubblicità personalizzata")}
          version={c.marketing.version}
          currentVersion={c.marketing.current_version}
          updatedAt={c.marketing.updated_at}
          accepted={c.marketing.accepted}
          optional
          onToggle={toggleMarketing}
          busy={busy}
        />
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        <div className="text-white/40">{_t("Puoi modificare o revocare il consenso marketing in qualsiasi momento. I consensi obbligatori possono essere revocati solo eliminando l'account.")}</div>
        <button onClick={() => setShowHistory((s) => !s)} className="text-white/60 hover:text-white flex items-center gap-1" data-testid="consent-history-toggle">
          <History className="w-3.5 h-3.5" /> {showHistory ? _t("Nascondi") : _t("Mostra")}{" "}{_t("storico")}</button>
      </div>

      {showHistory && (
        <div className="mt-4 border-t border-white/5 pt-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">{_t("Storico consensi")}</div>
          <ul className="space-y-2 max-h-72 overflow-y-auto">
            {history.length === 0 && <li className="text-sm text-white/40">{_t("Nessun evento registrato.")}</li>}
            {history.map((h) => (
              <li key={h.id} className="flex items-center gap-3 text-sm py-1.5 border-b border-white/5 last:border-0">
                {h.accepted ? <Check className="w-4 h-4 text-emerald-400 shrink-0" /> : <X className="w-4 h-4 text-red-400 shrink-0" />}
                <span className="text-white/80 capitalize w-24">{typeLabel(h.consent_type)}</span>
                <span className="text-white/40 text-xs">v{h.version}</span>
                <span className="text-white/40 text-xs">· {h.source}</span>
                <span className="text-white/40 text-xs ml-auto">{fmtDateTime(h.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function typeLabel(t) {
  return { terms: _t("Termini"), privacy: _t("Privacy"), marketing: _t("Marketing") }[t] || t;
}

function ConsentCard({ testId, icon, title, version, currentVersion, updatedAt, accepted, required, optional, needsUpdate, onToggle, onReAccept, busy }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] rounded-lg p-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {icon}
            <div className="text-white text-sm font-medium">{title}</div>
            {required && <span className="text-[9px] uppercase tracking-[0.2em] text-[#FF5722] border border-[#FF5722]/40 rounded px-1.5 py-0.5">{_t("Obbligatorio")}</span>}
            {optional && <span className="text-[9px] uppercase tracking-[0.2em] text-white/50 border border-white/20 rounded px-1.5 py-0.5">{_t("Facoltativo")}</span>}
          </div>
          <div className="text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1">
            <span className="flex items-center gap-1">
              {accepted ? <><Check className="w-3 h-3 text-emerald-400" />{" "}{_t("Accettato")}</> : <><X className="w-3 h-3 text-red-400" />{" "}{_t("Rifiutato")}</>}
            </span>
            <span>{_t("Versione accettata:")}{" "}<span className="text-white/80">v{version || "—"}</span></span>
            <span>{_t("Corrente:")}{" "}<span className="text-white/80">v{currentVersion}</span></span>
            <span>{_t("Aggiornato:")}{" "}<span className="text-white/80">{fmtDateTime(updatedAt)}</span></span>
          </div>
          {needsUpdate && (
            <div className="mt-2 text-xs text-yellow-400 flex items-center gap-2">{_t("È disponibile una nuova versione del documento (v{0}). Rivedi e ri-accetta.", currentVersion)}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onToggle && (
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                disabled={busy}
                onChange={(e) => onToggle(e.target.checked)}
                className="w-4 h-4 accent-[#FF5722]"
                data-testid={`${testId}-toggle`}
              />
              <span className="text-sm text-white/80">{accepted ? _t("Attivo") : _t("Disattivato")}</span>
            </label>
          )}
          {onReAccept && needsUpdate && (
            <button onClick={onReAccept} disabled={busy} className="btn-launch !text-xs !px-3 !py-1.5" data-testid={`${testId}-reaccept`}>{_t("Accetta v{0}", currentVersion)}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className="text-white">{value || "—"}</div>
    </div>
  );
}
